import { useEffect, useRef, useState, type ReactNode } from 'react';
import { audio } from '../audio/engine';
import { needsGyroPermission, requestGyro, setPrefs, useAvPrefs } from '../audio/prefs';

const Svg = ({ children }: { children: ReactNode }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
export const SoundIcon = ({ on }: { on: boolean }) => (
  <Svg>
    <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
    {on ? (
      <>
        <path d="M15.5 9a4 4 0 0 1 0 6" />
        <path d="M18 6.5a7.5 7.5 0 0 1 0 11" />
      </>
    ) : (
      <path d="M16 9.5l5 5M21 9.5l-5 5" />
    )}
  </Svg>
);
export const MotionIcon = ({ on }: { on: boolean }) => (
  <Svg>
    <rect x="7.5" y="3.5" width="9" height="17" rx="2.2" />
    {on ? (
      <>
        <path d="M3.5 9c-1 2-1 4 0 6" />
        <path d="M20.5 9c1 2 1 4 0 6" />
      </>
    ) : (
      <path d="M3.5 3.5l17 17" />
    )}
  </Svg>
);
const GearIcon = () => (
  <Svg>
    <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="10" cy="17" r="2" />
  </Svg>
);

/** Small persistent sound and motion toggles with a volume popover. */
export function AvControls() {
  const p = useAvPrefs();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => audio.applyVolumes(), [p.sound, p.music, p.sfx, p.voice]);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', esc);
    };
  }, [open]);

  const toggleSound = () => {
    const on = !p.sound;
    setPrefs({ sound: on, decided: true });
    if (on) {
      audio.unlock();
      audio.playSfx('ui_click');
    } else audio.applyVolumes();
  };
  const toggleMotion = () => {
    const on = !p.motion;
    setPrefs({ motion: on, decided: true });
    if (on && needsGyroPermission() && !p.gyroGranted) void requestGyro();
  };
  const slider = (key: 'music' | 'sfx' | 'voice', label: string) => (
    <label className="av-pop__row">
      <span>{label}</span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={p[key]}
        onChange={(e) => setPrefs({ [key]: Number(e.target.value) })}
        onPointerUp={() => key === 'sfx' && audio.playSfx('ui_click')}
        aria-label={`${label} volume`}
      />
    </label>
  );

  return (
    <div className="av-controls" ref={box}>
      <button type="button" className={`av-btn ${p.sound ? 'av-btn--on' : ''}`} aria-pressed={p.sound} onClick={toggleSound} title={p.sound ? 'Mute sound' : 'Turn sound on'}>
        <SoundIcon on={p.sound} />
        <span className="sr-only">Sound</span>
      </button>
      <button type="button" className={`av-btn ${p.motion ? 'av-btn--on' : ''}`} aria-pressed={p.motion} onClick={toggleMotion} title={p.motion ? 'Turn motion off' : 'Turn motion on'}>
        <MotionIcon on={p.motion} />
        <span className="sr-only">Motion</span>
      </button>
      <button type="button" className="av-btn" aria-expanded={open} aria-haspopup="dialog" onClick={() => setOpen((o) => !o)} title="Sound settings">
        <GearIcon />
        <span className="sr-only">Sound settings</span>
      </button>
      {open && (
        <div className="av-pop" role="dialog" aria-label="Sound settings">
          <label className="av-pop__row av-pop__row--toggle">
            <span>Sound</span>
            <input type="checkbox" checked={p.sound} onChange={toggleSound} />
          </label>
          {slider('music', 'Music')}
          {slider('sfx', 'Effects')}
          {slider('voice', 'Narrator')}
          <label className="av-pop__row av-pop__row--toggle">
            <span>Motion</span>
            <input type="checkbox" checked={p.motion} onChange={toggleMotion} />
          </label>
          <p className="av-pop__note">Narrator voice covers the prologue and Chapter One for now.</p>
        </div>
      )}
    </div>
  );
}
